import { Pipe, PipeTransform } from '@angular/core'
import { extractDeepPropertyByMapKey } from 'common/helpers'
import { OrderByPipe } from 'ngx-pipes'
import { naturalCompare } from './naturalOrder'

function isString (s): s is string {
  return typeof s === 'string' || s instanceof String
}

@Pipe({ name: 'naturalOrderBy' })
export class NaturalOrderByPipe implements PipeTransform {
  transform<T> (input: T[], sortPredicate: string|(string|((T) => any))[]|null = null): T[] {
    if (!Array.isArray(input)) {
      return input
    }

    const out = [...input]
    const cache = {}

    // sort by multiple properties
    if (Array.isArray(sortPredicate)) {
      return out.sort((a, b) => {
        for (const predicate of sortPredicate) {
          let pos = 0
          if (typeof predicate === 'function') {
            pos = naturalCompare(predicate(a), predicate(b), cache)
          } else {
            const [prop, asc] = OrderByPipe['extractFromConfig'](predicate)
            pos = this.orderCompare(prop, asc, a, b, cache)
          }
          if (pos !== 0) {
            return pos
          }
        }

        return 0
      })
    }

    // sort by a single property value
    if (isString(sortPredicate)) {
      const [prop, asc, sign] = OrderByPipe['extractFromConfig'](sortPredicate)

      if (sortPredicate.length === 1) {
        switch (sign) {
          case '+':
            return out.sort(this.simpleSort.bind(this))
          case '-':
            return out.sort(this.simpleSort.bind(this)).reverse()
        }
      }

      return out.sort((a, b) => this.orderCompare(prop, asc, a, b, cache))
    }

    // default sort by value
    return out.sort((a, b) => naturalCompare(a, b, cache))
  }

  private orderCompare (prop: string, asc: boolean, a: any, b: any, cache: any) {
    const first = extractDeepPropertyByMapKey(a, prop)
    const second = extractDeepPropertyByMapKey(b, prop)

    if (first === second) {
      return 0
    }

    if (first === undefined || first === '') {
      return 1
    }

    if (second === undefined || second === '') {
      return -1
    }

    if (isString(first) && isString(second)) {
      const pos = naturalCompare(first, second, cache)
      return asc ? pos : -pos
    }

    return asc ? first - second : second - first
  }

  simpleSort (a: any, b: any): number {
    return isString(a) && isString(b) ? a.toLowerCase().localeCompare(b.toLowerCase()) : a - b
  }
}
